import "./HotspotEditor.scss";
import { useState, useEffect, useCallback } from "react";
import { useHotspotEditor } from "../../contexts/useHotspotEditor";
import { useCurrentVariant } from "../../contexts/useCurrentVariant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
   faPlus, 
   faTrash, 
   faCopy, 
   faDownload, 
   faMousePointer,
   faCrosshairs,
   faArrowsAlt,
   faCompass,
   faChevronDown,
   faChevronUp,
   faCheck,
   faCode,
   faSave,
   faClipboard
} from "@fortawesome/free-solid-svg-icons";

const CONTENT_TYPES = [
   { value: "text", label: "Text" },
   { value: "video", label: "Video" },
   { value: "image", label: "Image" },
   { value: "link", label: "Link" },
   { value: "animation", label: "Animation" }
];

const VISIBILITY_OPTIONS = [
   { value: "dynamic", label: "Dynamic (shows/hides based on facing)" },
   { value: "always", label: "Always visible" },
   { value: "hidden", label: "Hidden" }
];

const LABEL_ALIGNMENTS = [
   { value: "top", label: "Top" },
   { value: "bottom", label: "Bottom" },
   { value: "left", label: "Left" },
   { value: "right", label: "Right" }
];

const VIDEO_MODES = [
   { value: "popup", label: "Popup Modal" },
   { value: "hotspot", label: "Inline Hotspot" }
];

const VIDEO_SOURCES = [
   { value: "fileUrl", label: "File URL" },
   { value: "youtube", label: "YouTube" },
   { value: "vimeo", label: "Vimeo" }
];

export default function HotspotEditor() {
   const {
      editorMode,
      setEditorMode,
      selectedHotspotIndex,
      setSelectedHotspotIndex,
      selectedHotspot,
      hotspots,
      isDirty,
      initializeHotspots,
      updateHotspot,
      deleteHotspot,
      duplicateHotspot,
      exportHotspots
   } = useHotspotEditor();

   const [currentVariant] = useCurrentVariant();
   const [expandedSections, setExpandedSections] = useState({
      position: true,
      content: true,
      video: false,
      animation: false,
      appearance: false
   });
   const [showExportModal, setShowExportModal] = useState(false);
   const [copySuccess, setCopySuccess] = useState(false);

   // Initialize hotspots from current variant
   useEffect(() => {
      if (currentVariant?.hotspots) {
         initializeHotspots(currentVariant.hotspots);
      }
   }, [currentVariant, initializeHotspots]);

   const toggleSection = (section) => {
      setExpandedSections(prev => ({
         ...prev,
         [section]: !prev[section]
      }));
   };

   const handleInputChange = useCallback((field, value) => {
      if (selectedHotspotIndex === null) return;
      updateHotspot(selectedHotspotIndex, { [field]: value });
   }, [selectedHotspotIndex, updateHotspot]);

   const handlePositionChange = useCallback((axis, value) => {
      if (selectedHotspotIndex === null || !selectedHotspot) return;
      const newPosition = { ...selectedHotspot.position };
      newPosition[axis] = `${value}m`;
      updateHotspot(selectedHotspotIndex, { position: newPosition });
   }, [selectedHotspotIndex, selectedHotspot, updateHotspot]);

   const handleNormalChange = useCallback((axis, value) => {
      if (selectedHotspotIndex === null || !selectedHotspot) return;
      const newNormal = { ...selectedHotspot.normal };
      newNormal[axis] = `${value}m`;
      updateHotspot(selectedHotspotIndex, { normal: newNormal });
   }, [selectedHotspotIndex, selectedHotspot, updateHotspot]);

   const parseCoordinate = (coord) => {
      if (typeof coord === 'string') {
         return parseFloat(coord.replace('m', '')) || 0;
      }
      return coord || 0;
   };

   const handleExport = () => {
      setShowExportModal(true);
   };

   const handleCopyToClipboard = async () => {
      try {
         await navigator.clipboard.writeText(exportHotspots());
         setCopySuccess(true);
         setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
         console.error('Failed to copy:', err);
      }
   };

   const handleDownloadJson = () => {
      const blob = new Blob([exportHotspots()], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hotspots.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
   };

   return (
      <div className="hotspot-editor">
         <div className="editor-header">
            <h2>Hotspot Editor</h2>
            {isDirty && <span className="unsaved-indicator">● Unsaved</span>}
         </div>

         {/* Mode Selection */}
         <div className="editor-modes">
            <button
               className={`mode-btn ${editorMode === 'select' ? 'active' : ''}`}
               onClick={() => setEditorMode('select')}
               title="Select Mode"
            >
               <FontAwesomeIcon icon={faMousePointer} />
               <span>Select</span>
            </button>
            <button
               className={`mode-btn ${editorMode === 'add' ? 'active' : ''}`}
               onClick={() => setEditorMode('add')}
               title="Add Hotspot Mode - Click on model to add"
            >
               <FontAwesomeIcon icon={faPlus} />
               <span>Add</span>
            </button>
            <button
               className={`mode-btn ${editorMode === 'edit-position' ? 'active' : ''}`}
               onClick={() => setEditorMode('edit-position')}
               disabled={selectedHotspotIndex === null}
               title="Edit Position - Click on model to move hotspot"
            >
               <FontAwesomeIcon icon={faArrowsAlt} />
               <span>Move</span>
            </button>
            <button
               className={`mode-btn ${editorMode === 'edit-normal' ? 'active' : ''}`}
               onClick={() => setEditorMode('edit-normal')}
               disabled={selectedHotspotIndex === null}
               title="Edit Normal - Click on model to set facing direction"
            >
               <FontAwesomeIcon icon={faCompass} />
               <span>Normal</span>
            </button>
         </div>

         {/* Mode Instructions */}
         <div className="mode-instructions">
            {editorMode === 'select' && <p>Click a hotspot to select it for editing</p>}
            {editorMode === 'add' && <p>Click anywhere on the model to add a new hotspot</p>}
            {editorMode === 'edit-position' && <p>Click on the model to move the selected hotspot</p>}
            {editorMode === 'edit-normal' && <p>Click on a surface to set the hotspot's facing direction</p>}
         </div>

         {/* Hotspot List */}
         <div className="hotspot-list">
            <div className="list-header">
               <h3>Hotspots ({hotspots.length})</h3>
               <button className="export-btn" onClick={handleExport} title="Export JSON">
                  <FontAwesomeIcon icon={faCode} />
               </button>
            </div>
            <div className="list-items">
               {hotspots.map((hotspot, index) => (
                  <div
                     key={hotspot.id}
                     className={`hotspot-item ${selectedHotspotIndex === index ? 'selected' : ''}`}
                     onClick={() => {
                        setSelectedHotspotIndex(index);
                        setEditorMode('select');
                     }}
                  >
                     <span className="hotspot-id">#{hotspot.id}</span>
                     <span className="hotspot-title">{hotspot.title || 'Untitled'}</span>
                     <span className="hotspot-type">{hotspot.contentType}</span>
                  </div>
               ))}
               {hotspots.length === 0 && (
                  <div className="empty-list">
                     No hotspots. Click "Add" then click on the model to create one.
                  </div>
               )}
            </div>
         </div>

         {/* Selected Hotspot Editor */}
         {selectedHotspot && (
            <div className="hotspot-details">
               <div className="details-header">
                  <h3>Edit Hotspot #{selectedHotspot.id}</h3>
                  <div className="header-actions">
                     <button
                        className="action-btn duplicate"
                        onClick={() => duplicateHotspot(selectedHotspotIndex)}
                        title="Duplicate"
                     >
                        <FontAwesomeIcon icon={faCopy} />
                     </button>
                     <button
                        className="action-btn delete"
                        onClick={() => deleteHotspot(selectedHotspotIndex)}
                        title="Delete"
                     >
                        <FontAwesomeIcon icon={faTrash} />
                     </button>
                  </div>
               </div>

               {/* Position & Normal Section */}
               <div className="editor-section">
                  <div className="section-header" onClick={() => toggleSection('position')}>
                     <span>Position & Normal</span>
                     <FontAwesomeIcon icon={expandedSections.position ? faChevronUp : faChevronDown} />
                  </div>
                  {expandedSections.position && (
                     <div className="section-content">
                        <div className="coord-group">
                           <label>Position</label>
                           <div className="coord-inputs">
                              <div className="coord-input">
                                 <span>X</span>
                                 <input
                                    type="number"
                                    step="0.001"
                                    value={parseCoordinate(selectedHotspot.position?.x)}
                                    onChange={(e) => handlePositionChange('x', e.target.value)}
                                 />
                              </div>
                              <div className="coord-input">
                                 <span>Y</span>
                                 <input
                                    type="number"
                                    step="0.001"
                                    value={parseCoordinate(selectedHotspot.position?.y)}
                                    onChange={(e) => handlePositionChange('y', e.target.value)}
                                 />
                              </div>
                              <div className="coord-input">
                                 <span>Z</span>
                                 <input
                                    type="number"
                                    step="0.001"
                                    value={parseCoordinate(selectedHotspot.position?.z)}
                                    onChange={(e) => handlePositionChange('z', e.target.value)}
                                 />
                              </div>
                           </div>
                           <button
                              className={`pick-btn ${editorMode === 'edit-position' ? 'active' : ''}`}
                              onClick={() => setEditorMode('edit-position')}
                           >
                              <FontAwesomeIcon icon={faCrosshairs} /> Pick from model
                           </button>
                        </div>

                        <div className="coord-group">
                           <label>Normal (facing direction)</label>
                           <div className="coord-inputs">
                              <div className="coord-input">
                                 <span>X</span>
                                 <input
                                    type="number"
                                    step="0.001"
                                    value={parseCoordinate(selectedHotspot.normal?.x)}
                                    onChange={(e) => handleNormalChange('x', e.target.value)}
                                 />
                              </div>
                              <div className="coord-input">
                                 <span>Y</span>
                                 <input
                                    type="number"
                                    step="0.001"
                                    value={parseCoordinate(selectedHotspot.normal?.y)}
                                    onChange={(e) => handleNormalChange('y', e.target.value)}
                                 />
                              </div>
                              <div className="coord-input">
                                 <span>Z</span>
                                 <input
                                    type="number"
                                    step="0.001"
                                    value={parseCoordinate(selectedHotspot.normal?.z)}
                                    onChange={(e) => handleNormalChange('z', e.target.value)}
                                 />
                              </div>
                           </div>
                           <button
                              className={`pick-btn ${editorMode === 'edit-normal' ? 'active' : ''}`}
                              onClick={() => setEditorMode('edit-normal')}
                           >
                              <FontAwesomeIcon icon={faCrosshairs} /> Pick from model
                           </button>
                        </div>
                     </div>
                  )}
               </div>

               {/* Content Section */}
               <div className="editor-section">
                  <div className="section-header" onClick={() => toggleSection('content')}>
                     <span>Content</span>
                     <FontAwesomeIcon icon={expandedSections.content ? faChevronUp : faChevronDown} />
                  </div>
                  {expandedSections.content && (
                     <div className="section-content">
                        <div className="form-group">
                           <label>Title</label>
                           <input
                              type="text"
                              value={selectedHotspot.title || ''}
                              onChange={(e) => handleInputChange('title', e.target.value)}
                           />
                        </div>

                        <div className="form-group">
                           <label>Content Type</label>
                           <select
                              value={selectedHotspot.contentType || 'text'}
                              onChange={(e) => handleInputChange('contentType', e.target.value)}
                           >
                              {CONTENT_TYPES.map(type => (
                                 <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                           </select>
                        </div>

                        <div className="form-group">
                           <label>Content / Description</label>
                           <textarea
                              rows={3}
                              value={selectedHotspot.content || ''}
                              onChange={(e) => handleInputChange('content', e.target.value)}
                           />
                        </div>

                        <div className="form-group">
                           <label>Visibility</label>
                           <select
                              value={selectedHotspot.visibility || 'dynamic'}
                              onChange={(e) => handleInputChange('visibility', e.target.value)}
                           >
                              {VISIBILITY_OPTIONS.map(opt => (
                                 <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                           </select>
                        </div>

                        <div className="form-group">
                           <label>Label Alignment</label>
                           <select
                              value={selectedHotspot.labelAlignment || 'right'}
                              onChange={(e) => handleInputChange('labelAlignment', e.target.value)}
                           >
                              {LABEL_ALIGNMENTS.map(align => (
                                 <option key={align.value} value={align.value}>{align.label}</option>
                              ))}
                           </select>
                        </div>
                     </div>
                  )}
               </div>

               {/* Video Settings (conditional) */}
               {selectedHotspot.contentType === 'video' && (
                  <div className="editor-section">
                     <div className="section-header" onClick={() => toggleSection('video')}>
                        <span>Video Settings</span>
                        <FontAwesomeIcon icon={expandedSections.video ? faChevronUp : faChevronDown} />
                     </div>
                     {expandedSections.video && (
                        <div className="section-content">
                           <div className="form-group">
                              <label>Video Mode</label>
                              <select
                                 value={selectedHotspot.videoMode || 'popup'}
                                 onChange={(e) => handleInputChange('videoMode', e.target.value)}
                              >
                                 {VIDEO_MODES.map(mode => (
                                    <option key={mode.value} value={mode.value}>{mode.label}</option>
                                 ))}
                              </select>
                           </div>

                           <div className="form-group">
                              <label>Video Source</label>
                              <select
                                 value={selectedHotspot.videoSource || 'fileUrl'}
                                 onChange={(e) => handleInputChange('videoSource', e.target.value)}
                              >
                                 {VIDEO_SOURCES.map(source => (
                                    <option key={source.value} value={source.value}>{source.label}</option>
                                 ))}
                              </select>
                           </div>

                           {selectedHotspot.videoSource === 'fileUrl' && (
                              <div className="form-group">
                                 <label>Video URL</label>
                                 <input
                                    type="text"
                                    value={selectedHotspot.videoUrl || ''}
                                    onChange={(e) => handleInputChange('videoUrl', e.target.value)}
                                    placeholder="https://example.com/video.mp4"
                                 />
                              </div>
                           )}

                           {selectedHotspot.videoSource === 'youtube' && (
                              <div className="form-group">
                                 <label>YouTube ID</label>
                                 <input
                                    type="text"
                                    value={selectedHotspot.youtubeId || ''}
                                    onChange={(e) => handleInputChange('youtubeId', e.target.value)}
                                    placeholder="dQw4w9WgXcQ"
                                 />
                              </div>
                           )}

                           {selectedHotspot.videoSource === 'vimeo' && (
                              <div className="form-group">
                                 <label>Vimeo ID</label>
                                 <input
                                    type="text"
                                    value={selectedHotspot.vimeoId || ''}
                                    onChange={(e) => handleInputChange('vimeoId', e.target.value)}
                                    placeholder="123456789"
                                 />
                              </div>
                           )}

                           <div className="form-row">
                              <div className="form-group checkbox">
                                 <label>
                                    <input
                                       type="checkbox"
                                       checked={selectedHotspot.videoAutoplay ?? true}
                                       onChange={(e) => handleInputChange('videoAutoplay', e.target.checked)}
                                    />
                                    Autoplay
                                 </label>
                              </div>
                              <div className="form-group checkbox">
                                 <label>
                                    <input
                                       type="checkbox"
                                       checked={selectedHotspot.closeOnVideoEnded ?? false}
                                       onChange={(e) => handleInputChange('closeOnVideoEnded', e.target.checked)}
                                    />
                                    Close on end
                                 </label>
                              </div>
                           </div>
                        </div>
                     )}
                  </div>
               )}

               {/* Animation Settings (conditional) */}
               {selectedHotspot.contentType === 'animation' && (
                  <div className="editor-section">
                     <div className="section-header" onClick={() => toggleSection('animation')}>
                        <span>Animation Settings</span>
                        <FontAwesomeIcon icon={expandedSections.animation ? faChevronUp : faChevronDown} />
                     </div>
                     {expandedSections.animation && (
                        <div className="section-content">
                           <div className="form-group">
                              <label>Animation Name</label>
                              <input
                                 type="text"
                                 value={selectedHotspot.animationName || ''}
                                 onChange={(e) => handleInputChange('animationName', e.target.value)}
                                 placeholder="animation_clip_name"
                              />
                           </div>

                           <div className="form-row">
                              <div className="form-group">
                                 <label>Repetitions</label>
                                 <input
                                    type="number"
                                    min="1"
                                    value={selectedHotspot.animationRepetitions || 1}
                                    onChange={(e) => handleInputChange('animationRepetitions', parseInt(e.target.value))}
                                 />
                              </div>
                              <div className="form-group">
                                 <label>Speed</label>
                                 <input
                                    type="number"
                                    step="0.1"
                                    min="0.1"
                                    value={selectedHotspot.animationSpeed || 1}
                                    onChange={(e) => handleInputChange('animationSpeed', parseFloat(e.target.value))}
                                 />
                              </div>
                           </div>

                           <div className="form-group">
                              <label>Animation Mode</label>
                              <select
                                 value={selectedHotspot.animationMode || 'loop'}
                                 onChange={(e) => handleInputChange('animationMode', e.target.value)}
                              >
                                 <option value="loop">Loop</option>
                                 <option value="pingpong">Ping Pong</option>
                                 <option value="once">Once</option>
                              </select>
                           </div>

                           <div className="form-group">
                              <label>Caption</label>
                              <input
                                 type="text"
                                 value={selectedHotspot.caption || ''}
                                 onChange={(e) => handleInputChange('caption', e.target.value)}
                              />
                           </div>

                           <div className="form-row">
                              <div className="form-group">
                                 <label>Caption Duration (ms)</label>
                                 <input
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={selectedHotspot.captionDuration || 3000}
                                    onChange={(e) => handleInputChange('captionDuration', parseInt(e.target.value))}
                                 />
                              </div>
                              <div className="form-group">
                                 <label>Delay (ms)</label>
                                 <input
                                    type="number"
                                    min="0"
                                    step="100"
                                    value={selectedHotspot.delay || 0}
                                    onChange={(e) => handleInputChange('delay', parseInt(e.target.value))}
                                 />
                              </div>
                           </div>

                           <div className="form-row">
                              <div className="form-group checkbox">
                                 <label>
                                    <input
                                       type="checkbox"
                                       checked={selectedHotspot.resetCamera ?? false}
                                       onChange={(e) => handleInputChange('resetCamera', e.target.checked)}
                                    />
                                    Reset camera
                                 </label>
                              </div>
                              <div className="form-group checkbox">
                                 <label>
                                    <input
                                       type="checkbox"
                                       checked={selectedHotspot.isolate ?? false}
                                       onChange={(e) => handleInputChange('isolate', e.target.checked)}
                                    />
                                    Isolate (hide others)
                                 </label>
                              </div>
                           </div>
                        </div>
                     )}
                  </div>
               )}

               {/* Image Settings (conditional) */}
               {selectedHotspot.contentType === 'image' && (
                  <div className="editor-section">
                     <div className="section-header" onClick={() => toggleSection('image')}>
                        <span>Image Settings</span>
                        <FontAwesomeIcon icon={expandedSections.image ? faChevronUp : faChevronDown} />
                     </div>
                     {expandedSections.image && (
                        <div className="section-content">
                           <div className="form-group">
                              <label>Image URL</label>
                              <input
                                 type="text"
                                 value={selectedHotspot.imageUrl || ''}
                                 onChange={(e) => handleInputChange('imageUrl', e.target.value)}
                                 placeholder="https://example.com/image.png"
                              />
                           </div>
                        </div>
                     )}
                  </div>
               )}

               {/* Link Settings (conditional) */}
               {selectedHotspot.contentType === 'link' && (
                  <div className="editor-section">
                     <div className="section-header" onClick={() => toggleSection('link')}>
                        <span>Link Settings</span>
                        <FontAwesomeIcon icon={expandedSections.link ? faChevronUp : faChevronDown} />
                     </div>
                     {expandedSections.link && (
                        <div className="section-content">
                           <div className="form-group">
                              <label>Link URL</label>
                              <input
                                 type="text"
                                 value={selectedHotspot.linkUrl || ''}
                                 onChange={(e) => handleInputChange('linkUrl', e.target.value)}
                                 placeholder="https://example.com"
                              />
                           </div>
                        </div>
                     )}
                  </div>
               )}

               {/* Appearance Section */}
               <div className="editor-section">
                  <div className="section-header" onClick={() => toggleSection('appearance')}>
                     <span>Appearance</span>
                     <FontAwesomeIcon icon={expandedSections.appearance ? faChevronUp : faChevronDown} />
                  </div>
                  {expandedSections.appearance && (
                     <div className="section-content">
                        <div className="form-group">
                           <label>Icon Override</label>
                           <input
                              type="text"
                              value={selectedHotspot.iconOverride || ''}
                              onChange={(e) => handleInputChange('iconOverride', e.target.value)}
                              placeholder="e.g., video, text, link"
                           />
                        </div>

                        <div className="form-group">
                           <label>FontAwesome Icon Name</label>
                           <input
                              type="text"
                              value={selectedHotspot.fontAwesomeIconName || ''}
                              onChange={(e) => handleInputChange('fontAwesomeIconName', e.target.value)}
                              placeholder="e.g., faPlay, faInfo"
                           />
                        </div>

                        <div className="form-group">
                           <label>FontAwesome Icon Style</label>
                           <select
                              value={selectedHotspot.fontAwesomeIconStyle || 'solid'}
                              onChange={(e) => handleInputChange('fontAwesomeIconStyle', e.target.value)}
                           >
                              <option value="solid">Solid</option>
                              <option value="regular">Regular</option>
                              <option value="brands">Brands</option>
                           </select>
                        </div>
                     </div>
                  )}
               </div>
            </div>
         )}

         {/* Save/Export Section - Always visible at bottom */}
         <div className="save-section">
            <div className="save-section-info">
               <h4>
                  <FontAwesomeIcon icon={faSave} />
                  Save Your Work
               </h4>
               <p>Copy JSON to paste into your config file, or download as a file.</p>
            </div>
            <div className="save-section-actions">
               <button 
                  className={`save-btn primary ${copySuccess ? 'success' : ''}`} 
                  onClick={handleCopyToClipboard}
               >
                  <FontAwesomeIcon icon={copySuccess ? faCheck : faClipboard} />
                  {copySuccess ? 'Copied!' : 'Copy JSON'}
               </button>
               <button className="save-btn secondary" onClick={handleDownloadJson}>
                  <FontAwesomeIcon icon={faDownload} />
                  Download
               </button>
               <button className="save-btn tertiary" onClick={handleExport}>
                  <FontAwesomeIcon icon={faCode} />
                  Preview
               </button>
            </div>
         </div>

         {/* Export Modal */}
         {showExportModal && (
            <div className="export-modal-overlay" onClick={() => setShowExportModal(false)}>
               <div className="export-modal" onClick={(e) => e.stopPropagation()}>
                  <div className="export-modal-header">
                     <h3>Hotspots JSON Preview</h3>
                     <button className="close-btn" onClick={() => setShowExportModal(false)}>×</button>
                  </div>
                  <div className="export-modal-instructions">
                     <p>Copy this JSON and paste it into your config file under the variant's <code>hotspots</code> array:</p>
                  </div>
                  <div className="export-modal-content">
                     <pre>{exportHotspots()}</pre>
                  </div>
                  <div className="export-modal-actions">
                     <button className="copy-btn" onClick={handleCopyToClipboard}>
                        <FontAwesomeIcon icon={copySuccess ? faCheck : faCopy} />
                        {copySuccess ? 'Copied!' : 'Copy to Clipboard'}
                     </button>
                     <button className="download-btn" onClick={handleDownloadJson}>
                        <FontAwesomeIcon icon={faDownload} />
                        Download JSON
                     </button>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}

